import i18next from 'i18next';

import {history} from "./history";
import {alertActionTypes, notificationConstants} from "../_constants";
import {__isAvailable, __isKeyAvailable} from "./commons/utility.helper";

/**
 * Notification helper that can serve as a application-wide global notification system
 */
export const notificationHelper = {
    handleAlert,
};

/**
 * Returns default values for Notification System
 */
const getDefaultValues = () => {
    const t = i18next.t;

    return {
        TOAST : {
            PERSISTENCE: false,                     // toast will close after timer runs out

            APPEARANCE: notificationConstants.NOTIFICATION_TYPES.INFO,  // use info as fallback

            MESSAGE: {
                SUCCESS: t("Success"),              // toast success 'default' text
                ERROR: t("Error"),                  // toast error 'default' text
            }
        }
    }
}

/**
 * Returns the toast message for the current alert
 *
 * @param alert the alert that needs to be parsed for the message
 * @return {string} the toast message
 */
const getToastMessage = (alert) => {
    let message = "";

    const alertMessage = alert.message;
    if(__isKeyAvailable(alertMessage, "successMsg")) {
        message = alertMessage.successMsg;
    } else if (__isKeyAvailable(alertMessage, "errorMsg")) {
        message = alertMessage.errorMsg
    }

    // fallback
    const stringUtils = require('underscore.string');
    if (stringUtils.isBlank(message)) {
        if(alert.type === alertActionTypes.SUCCESS) {
            message = getDefaultValues().TOAST.MESSAGE.SUCCESS;
        } else if(alert.type === alertActionTypes.ERROR) {
            message = getDefaultValues().TOAST.MESSAGE.ERROR;
        }
    }

    return message;
}

/**
 * Returns the toast persistence preference
 *
 * @param toastParams the param that may contain preference
 * @return {*} toast persistence preference
 */
const getToastPersistence = (toastParams) => {
    return (__isKeyAvailable(toastParams, "toastPersistence"))
                ? toastParams.toastPersistence              // preference
                : getDefaultValues().TOAST.PERSISTENCE;     // fallback preference
}

/**
 * Returns the appearance for the toast
 *
 * @param toastParams the parameters that determines the type - expected "toastType" property
 * @return {string} toast-type
 */
const getToastAppearance = (toastParams) => {
    let toastAppearance = getDefaultValues().TOAST.APPEARANCE;  // fallback
    if(__isKeyAvailable(toastParams, "toastType")) {
        switch(toastParams.toastType) {
            case notificationConstants.NOTIFICATION_TYPES.SUCCESS:
            case notificationConstants.NOTIFICATION_TYPES.INFO:
            case notificationConstants.NOTIFICATION_TYPES.WARN:
            case notificationConstants.NOTIFICATION_TYPES.ERROR:
                toastAppearance = toastParams.toastType;        // use if from the expected list
                break;

            default:
                toastAppearance = getDefaultValues().TOAST.APPEARANCE;  // fallback if not expected
        }
    }
    return toastAppearance;
}

/**
 * This function is a global error notification helper
 * @param alert the alert object
 * @param notifierParams the object containing toastManager, translation and clearAlert object
 */
function handleAlert(alert, notifierParams){

    if(__isAvailable(alert.message.toastParams) || __isAvailable(alert.message.redirectParams)) {

        // perform redirection if needed
        if(__isKeyAvailable(alert.message, "redirectParams", "redirectTo")) {
            // check if external redirect
            let isExternalRedirect = (__isKeyAvailable(alert.message, "redirectParams", "isExternal"))
                                            ? alert.message.redirectParams.isExternal
                                            : false;
            // reload page if external else route within app
            if(isExternalRedirect) {
                setTimeout(() => {
                    window.location.replace(alert.message.redirectParams.redirectTo);
                }, 2000)    // redirect after 2000 ms
            } else {
                history.push(alert.message.redirectParams.redirectTo);
            }
        }

        // perform toast
        const toastParams = {
            'message': getToastMessage(alert),
            'isAutoDismiss': !getToastPersistence(alert.message.toastParams),
            'appearance': getToastAppearance(alert.message.toastParams)
        };
        toast(toastParams, notifierParams); // Toast it
    }
}

/**
 * Generic Toast-er that accepts parameters and displays it
 *
 * @param toastParams       the toast parameters
 * @param notifierParams    the notification-related helpers
 */
function toast(toastParams, notifierParams) {
    // pull parameters
    const {
        message = "",
        appearance = getDefaultValues().TOAST.APPEARANCE,
        isAutoDismiss = !getDefaultValues().TOAST.PERSISTENCE,
    } = toastParams;

    // pull handlers
    const {toastManager, t, clearAlert} = notifierParams;

    // add the toast
    toastManager.add(
        t(message),
        {
            appearance: appearance,
            autoDismiss: isAutoDismiss,
        }
    );

    // clear alert after displaying
	clearAlert();
}